import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import SuccessModal from '../../components/modals/SuccessModal';
import CreatePasswordModal from '../../components/modals/CreatePasswordModal';
import JWTManager from '../../utils/jwtUtils';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  
  const [resetToken, setResetToken] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successStatus, setSuccessStatus] = useState<'success' | 'failed'>('success');
  const [tokenProcessed, setTokenProcessed] = useState(false);

  // Helper function to fix JWT token URL encoding issues
  const fixJwtTokenFromUrl = (token: string): string => {
    // Fix JWT token encoding issue: + characters get converted to spaces in URLs
    // We need to restore the + characters that represent / in base64 encoding
    return token.replace(/\s/g, '+');
  };

  // Extract token from URL parameters and validate expiration
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      console.log('Original token from URL:', token);
      const fixedToken = fixJwtTokenFromUrl(token);
      console.log('Fixed token:', fixedToken);
      
      // Validate token expiration client-side before showing form
      try {
        if (JWTManager.isTokenExpired(fixedToken)) {
          // Token is expired, show error immediately
          setSuccessMessage('For your security, this password reset link has expired. Please request a new link and we’ll send it to your inbox.');
          setSuccessStatus('failed');
          setShowSuccess(true);
          setShowCreatePassword(false);
          setTokenProcessed(true);
          return;
        }
        
        // Token is valid, proceed with showing the form
        setResetToken(fixedToken);
        setShowCreatePassword(true);
        setTokenProcessed(true);
      } catch (error) {
        // Token is invalid or malformed
        console.error('Token validation error:', error);
        setSuccessMessage('This password reset link is no longer valid. It may have already been used. Please request a new link to continue.');
        setSuccessStatus('failed');
        setShowSuccess(true);
        setShowCreatePassword(false);
        setTokenProcessed(true);
      }
    } else {
      // If no token, redirect to login
      navigate('/login');
    }
  }, [searchParams, navigate]);

    const handleCreatePasswordSubmit = async (password: string, confirmPassword: string) => {
      setIsLoading(true);
    try {
      const result = await resetPassword(
        resetToken,
        password,
        confirmPassword
      );

      if (result.success) {
        setSuccessMessage('Your password has been updated. Redirecting you to sign in...');
        setSuccessStatus('success');
        window.innerWidth > 768 && setShowCreatePassword(false);
        setShowSuccess(true);

        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Check if token is expired or invalid
        const errorMessage = result.error || 'Please try again.';
        const isTokenExpired = errorMessage.toLowerCase().includes('expired') || 
                              errorMessage.toLowerCase().includes('invalid') ||
                              errorMessage.toLowerCase().includes('token') ||
                              errorMessage.toLowerCase().includes('unauthorized') ||
                              errorMessage.toLowerCase().includes('forbidden');
        
        if (isTokenExpired) {
          setSuccessMessage('For your security, this password reset link is no longer valid. Please request a new link and we’ll send it to your inbox.');
          setSuccessStatus('failed');
        } else {
          setSuccessMessage(errorMessage);
          setSuccessStatus('failed');
        }
        window.innerWidth > 768 && setShowCreatePassword(false);
        setShowSuccess(true);
      }
    } catch (error) {
      // Handle network errors or unexpected errors
      let errorMessage = 'Something went wrong on our end. Please try again in a moment.';
      
      if (error instanceof Error) {
        const errorText = error.message.toLowerCase();
        if (errorText.includes('expired') || errorText.includes('invalid') || errorText.includes('token')) {
          errorMessage = 'For your security, this password reset link is no longer valid. Please request a new link and we’ll send it to your inbox.';
        } else if (errorText.includes('network') || errorText.includes('fetch')) {
          errorMessage = 'We couldn’t reach the server. Please check your internet connection and try again.';
        } else {
          errorMessage = error.message || errorMessage;
        }
      }
      
      setSuccessMessage(errorMessage);
      setSuccessStatus('failed');
      window.innerWidth > 768 && setShowCreatePassword(false);
      setShowSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };


  const handleSuccessClose = () => {
    setShowSuccess(false);
    setShowCreatePassword(true);
    if (successStatus === 'success') {
    navigate('/login');
    }
  };

  const handleCreatePasswordClose = () => {
    setShowCreatePassword(false);
    navigate('/login');
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleRequestNewLink = () => {
    // Navigate to login page - user can use forgot password from there
    navigate('/login?forgotPassword=true');
  };

  // If token hasn't been processed yet, don't render (will process in useEffect)
  if (!tokenProcessed) {
    return null;
  }

  // Detect link-related failures (expired, invalid, no longer valid) — drives a
  // warmer title and shows the "Send New Link" secondary action.
  const isLinkFailure =
    successStatus === 'failed' &&
    /expired|invalid|no longer valid|link/i.test(successMessage);

  return (
    <div className="min-h-screen h-full flex items-center justify-center">
      <picture className="absolute inset-0 z-0">
        <source media="(max-width: 480px)" srcSet={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-mobile.webp`} />
        <source media="(max-width: 920px)" srcSet={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-tablet.webp`} />
        <img
          src={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-desktop.webp`}
          alt=""
          className="w-full h-full object-cover"
        />
      </picture>

      {/* Create Password Modal */}
      <CreatePasswordModal
        isOpen={showCreatePassword}
        onClose={handleCreatePasswordClose}
        onSubmit={handleCreatePasswordSubmit}
        isLoading={isLoading}
      />

      {/* Success/Error Modal */}
      <SuccessModal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        title={
          successStatus === 'success'
            ? 'Password Updated'
            : isLinkFailure
              ? 'Reset Link Expired'
              : 'Couldn’t Reset Password'
        }
        subtitle={successMessage}
        status={successStatus}
        actionText={successStatus === 'success' ? 'Sign In' : 'Back to Sign In'}
        onAction={successStatus === 'success' ? handleSuccessClose : handleBackToLogin}
        secondaryActionText={isLinkFailure ? 'Send New Link' : undefined}
        onSecondaryAction={isLinkFailure ? handleRequestNewLink : undefined}
      />
    </div>
  );
};

export default ResetPasswordPage; 